import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet} from "@angular/router";
import {concatMap, filter, of, Subscription} from "rxjs";

// Сервисы
import {AppService} from "src/app/shared/services/app.service";
import {FormService} from "src/app/shared/services/form.service";

import {CacheService} from "src/app/shared/services/cache.service";
import {NavigationService} from "src/app/shared/services/navigation.service";
import {AlfaIdService} from "landings/osago-ab/app/alfa-id/alfa-id.service";
import {AccordionService} from "src/app/shared/services/accordion.service";
import {YandexMetrikaService} from "src/app/shared/services/yandex-metrika.service";

// Компоненты
import {AdvantagesComponent} from "src/app/shared/components/advantages/advantages.component";
import {FaqComponent} from "src/app/shared/components/faq/faq.component";
import {
  ProlongationAlfaidComponent
} from "landings/osago-ab/app/alfa-id/prolongation-alfaid/prolongation-alfaid.component";
import { CompanySwiperAbComponent } from 'landings/osago-ab/app/components/company-swiper-ab/company-swiper-ab.component';
import {FooterComponent} from "landings/osago-ab/app/components/footer/footer.component";
import { RefusalTrafficAlfaidComponent } from 'landings/osago-ab/app/alfa-id/refusal-traffic-alfaid/refusal-traffic-alfaid.component';
import {AbBannerComponent} from "landings/osago-ab/app/components/ab-banner/ab-banner.component";

// Интерфейсы
import {ROUTES} from "src/app/shared/enums/routes.enum";
import {ApiResponse} from "src/app/shared/interface/api-response";
import {IUserAlfaId} from "src/app/shared/interface/user-alfa-id";
import {YandexMetrikaGoalsEnum} from "src/app/shared/enums/yandex-metrika-goals.enum";
import {getTelegramWebApp} from "src/app/shared/functions/getTelegramWebApp";
import {listSomeValue} from "src/app/shared/functions/listSomeValue";


@Component({
  selector: 'app-root',
  templateUrl: '../../osago-ab/app/app.component.html',
  standalone: true,
  imports: [
    CompanySwiperAbComponent,
    RouterOutlet,
    AdvantagesComponent,
    FaqComponent,
    FooterComponent,
    AbBannerComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../osago-ab/app/app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  private appService = inject(AppService);
  private cacheService = inject(CacheService);
  private router = inject(Router);
  public formService = inject(FormService);
  private readonly navigationService = inject(NavigationService);
  private readonly route = inject(ActivatedRoute);
  private readonly alfaIdService = inject(AlfaIdService);
  private readonly accordionService = inject(AccordionService);
  private readonly ym = inject(YandexMetrikaService);

  protected readonly getTelegramWebApp = getTelegramWebApp;

  // Подписка
  private subscription = new Subscription();

  // Индикатор загрузки
  public isLoading!: boolean;

  // Если главная страница
  public isMainPage = false;
  // Ошибка AlfaID
  public alfaIdError = false;

  // --------------------------------------------------------------------------
  // Инициализация
  public ngOnInit(): void {
    this.replaceUrlForProlongationAlfaIDRoute();
    this.addProlongationAlfaIDRoute();
    this.addRefusalTrafficAlfaIDRoute();

    this.initApp();

    this.subscription.add(
      this.route.queryParams
        .subscribe((params) => {
          for (const objectKey in params) {
            if (objectKey.toLowerCase() === 'prolongation') {
              if (params[objectKey] === 'true') {
                this.router.navigate(['prolongation-alfaid'], {
                  queryParams: {...params, Prolongation: false}
                });
              }
            } else if (objectKey.toLowerCase() === 'refusaltraffic') {
              if (params[objectKey] === 'true') {
                this.router.navigate(['refusalTraffic-alfaid'], {
                  queryParams: {...params, refusalTraffic: false}
                });
              }
            } else if (objectKey.toLowerCase() === 'code') {
              if (params[objectKey] === 'true') {
              }
            }
          }
        })
    );

    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.isMainPage = this.router.url?.split('?')[0] === '/';
      })
    );
  }

  // Уничтожение
  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // --------------------------------------------------------------------------

  // Инициализация необходимых данных перед стартом
  private initApp(): void {
    this.isLoading = true;
    this.alfaIdError = false;
    this.appService.isShowBtnAlfaId = true;

    const urlParams = this.appService.getUrlParams();

    this.subscription.add(
      this.appService.loadApp()
        .pipe(
          concatMap(() => {
            const isRouteProlongationAlfaId = this.router.url.split('/').pop()?.includes('prolongation-alfaid');
            const isRouteRefusalTrafficAlfaId = this.router.url.split('/').pop()?.includes('refusalTraffic-alfaid');
            const isRouteSuccessPage = this.router.url.split('/').pop()?.includes('success');

            if (urlParams.code && !isRouteProlongationAlfaId && !isRouteSuccessPage && !isRouteRefusalTrafficAlfaId) {
              return this.alfaIdService.getAlfaUseFromCode(false);
            } else {
              return of(null);
            }
          }),
          concatMap((alfaIdData: ApiResponse<IUserAlfaId>) => {
            const isRouteProlongationAlfaId = this.router.url.split('/').pop()?.includes('prolongation-alfaid');
            const isRouteSuccessPage = this.router.url.split('/').pop()?.includes('success');

            if (alfaIdData === null) {
              return of(null);
            }

            if (!alfaIdData?.result || alfaIdData?.value?.error !== null) {
              this.ym.onYandexReachGoal(YandexMetrikaGoalsEnum.AlfaIdError);
              this.alfaIdError = true;
              return of(null);
            }

            if (alfaIdData && alfaIdData?.result && urlParams?.code && alfaIdData && !isRouteProlongationAlfaId && !isRouteSuccessPage) {
              return this.cacheService.getOsagoFromLocalStorage(urlParams, alfaIdData.value).pipe(
                concatMap(() => this.appService.getCarModels()),
              );
            } else {
              return of(null);
            }
          }),
        )
        .subscribe( {
          next: (res) => {
            const isRouteProlongationAlfaId = this.router.url.split('/').pop()?.includes('prolongation-alfaid');
            const isRouteRefusalTrafficAlfaId = this.router.url.split('/').pop()?.includes('refusalTraffic-alfaid');
            const isRouteSuccessPage = this.router.url.split('/').pop()?.includes('success');
            if (urlParams !== null
              && ((urlParams.applicationId
                && urlParams.src
                && urlParams.src !== 'Osago2Clicks'
                && urlParams.src !== 'DataCheckingBeforePaymentSms'
                && urlParams.offerId !== '0') || urlParams.alfaOfferId)) {
              const checkProductionList = listSomeValue(this.formService.licenseFormGroup?.get('productionYear')!, this.appService.productionYearsList);
              const checkModificationList = listSomeValue(this.formService.licenseFormGroup?.get('horsePower')!, this.appService.modificationsList);
              const checkModificationKwList = listSomeValue(this.formService.licenseFormGroup?.get('horsePowerKw')!, this.appService.modificationsListKwt);
              if (checkProductionList || (checkModificationList && checkModificationKwList)) {
                this.navigationService.navigate(ROUTES.Form);
              } else {
                this.navigationService.navigate(ROUTES.Offers);
              }
              this.isLoading = false;
            } else if (res && urlParams !== null && urlParams.code && !isRouteProlongationAlfaId && !isRouteSuccessPage && !isRouteRefusalTrafficAlfaId) {
              setTimeout(() => {
                this.isLoading = false;
                this.appService.isShowBtnAlfaId = false;
                this.navigationService.navigate(ROUTES.Form);
                setTimeout(() => {
                  this.accordionService.accordionGroups.get(1)!.isOpen = true;
                }, 100);
              }, 100);

            } else if (urlParams !== null && urlParams.src === 'Osago2Clicks' && !isRouteProlongationAlfaId && !isRouteSuccessPage && !isRouteRefusalTrafficAlfaId) {
              setTimeout(() => {
                this.isLoading = false;
                this.appService.isShowBtnAlfaId = false;
                this.formService.drivers.controls.forEach((driver, index) => {
                  // TODO тут нужно убрать валидацию в контроле "Дата выдачи паспорта"
                });
                this.navigationService.navigate(ROUTES.Form);
                setTimeout(() => {
                  const lastAccordionItem = this.accordionService.accordionGroups.length - 1;
                  this.accordionService.accordionGroups.get(lastAccordionItem)!.isOpen = true;
                }, 100);
              }, 100);
            } else if (this.alfaIdError && !isRouteSuccessPage && !isRouteRefusalTrafficAlfaId) {
              this.isLoading = false;
              this.navigationService.navigate(ROUTES.MainPage);

              this.alfaIdError = false;
            } else {
              this.isLoading = false;
            }
          },
          error: (e) => {
            this.isLoading = false;
          }
        })
    );
  }


  // Добавляем страницу пролонгации alfaID
  public addProlongationAlfaIDRoute() {
    const newRoute = {
      path: 'prolongation-alfaid',
      component: ProlongationAlfaidComponent
    };

    this.router.config.push(newRoute);
  }

  private replaceUrlForProlongationAlfaIDRoute() {
    const currentUrl = window.location.href;

    // Ищем первый ?code и меняем его на &code
    const modifiedUrl = currentUrl.replace('?code', '&code');

    // Если ? не является первым символом (то есть если есть другие параметры перед ?code)
    if (currentUrl.includes('?code') && !currentUrl.includes('?') || currentUrl.indexOf('?code') !== currentUrl.indexOf('?')) {
      window.history.replaceState(null, '', modifiedUrl);
    }
  }

  public addRefusalTrafficAlfaIDRoute() {
    const newRoute = {
      path: 'refusalTraffic-alfaid',
      component: RefusalTrafficAlfaidComponent
    };
    this.router.config.push(newRoute);
  }
}
